/**
 * Phase 7C-4 -- THE single place a bearer token enters an outgoing
 * request, and the single place a dead session is noticed.
 *
 * Every `src/api/*.ts` module calls the default `axios` export directly
 * (eleven of them, each with its own copied `getHeaders`). Rather than
 * thread a token through every one of them -- and forget the twelfth --
 * this installs two interceptors on that default instance once:
 *
 *   request   attaches `Authorization: Bearer <token>` when a token is in
 *             sessionStorage and the call did not opt out with
 *             `skipAuth: true` (only SESSION_CREATE does);
 *   response  when a request that CARRIED our token comes back 401, tells
 *             the auth controller the session is gone.
 *
 * The bearer is sent on business routes too. The Backend ignores it there
 * until Phase 7C-5 turns enforcement on, and sending it now means 7C-5
 * needs no Website change for the header. It costs nothing on the wire:
 * those requests are already CORS-preflighted by `operation`/`key-id`.
 *
 * WHY `sentBearer` IS TRACKED
 * ----------------------------------------------------------------------
 * A 401 on SESSION_CREATE means "wrong password" and must NOT sign anyone
 * out. A 401 on a request that carried the session token means "the
 * session the app believes in no longer exists". The request interceptor
 * records which case it is on the config, so the response interceptor
 * can tell them apart without guessing from the URL.
 *
 * NOTHING HERE LOGS A HEADER. The config object is never printed.
 */
import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { readSessionToken } from './session-store';

declare module 'axios' {
    export interface AxiosRequestConfig {
        /** Phase 7C-4: do not attach a bearer (the public SESSION_CREATE). */
        skipAuth?: boolean;
        /** Phase 7C-4: set by the request interceptor; true iff a bearer went out. */
        sentBearer?: boolean;
    }
}

/** The Backend's `reason` on a 401, when it disclosed one. */
export type UnauthenticatedReason = 'revokedByAdmin' | null;

export type UnauthenticatedHandler = (reason: UnauthenticatedReason) => void;

let unauthenticatedHandler: UnauthenticatedHandler | null = null;
const installedOn: WeakSet<object> = new WeakSet();

/**
 * Registers the one function to call when a bearer request is refused
 * with 401. Set once by `App.tsx` to the auth controller, which collapses
 * concurrent 401s into a single sign-out.
 */
export const setUnauthenticatedHandler = (handler: UnauthenticatedHandler | null): void => {
    unauthenticatedHandler = handler;
};

/** For tests, so one test's handler can never leak into the next. */
export const resetAuthHooks = (): void => {
    unauthenticatedHandler = null;
};

/** Request interceptor. Exported so it can be unit-tested directly. */
export const attachBearer = <T extends InternalAxiosRequestConfig>(config: T): T => {
    if (config.skipAuth) {
        config.sentBearer = false;
        return config;
    }
    // Read per request, never cached: a sign-out takes effect on the very
    // next call instead of leaving a stale credential in a closure.
    const token = readSessionToken();
    if (!token) {
        config.sentBearer = false;
        return config;
    }
    const value = `Bearer ${token}`;
    const headers = config.headers as unknown as { set?: (name: string, v: string) => void } | undefined;
    if (headers && typeof headers.set === 'function') {
        headers.set('Authorization', value);
    } else {
        config.headers = { ...(config.headers as object || {}), Authorization: value } as T['headers'];
    }
    config.sentBearer = true;
    return config;
};

/**
 * How the Backend described a 401, mapped to what the UI needs to say.
 *
 * The wire value is `ADMIN` -- the `employee_session.revoked_reason` the
 * Backend is willing to disclose. Mapped here rather than passed through so
 * a new server-side reason cannot reach the store as an unknown string, and
 * so the store's vocabulary stays something the login page can exhaust.
 */
export const unauthenticatedReason = (error: unknown): UnauthenticatedReason => {
    const candidate = error as { response?: { data?: { reason?: unknown } } } | null | undefined;
    const reason = candidate?.response?.data?.reason;
    return typeof reason === 'string' && reason.trim().toUpperCase() === 'ADMIN'
        ? 'revokedByAdmin'
        : null;
};

/** True iff [error] is a 401 on a request that carried the session token. */
export const isBearerUnauthenticated = (error: unknown): boolean => {
    const candidate = error as { config?: { sentBearer?: boolean }; response?: { status?: number } } | null | undefined;
    return !!(candidate
        && candidate.config
        && candidate.config.sentBearer === true
        && candidate.response
        && candidate.response.status === 401);
};

/** Response error interceptor. Exported so it can be unit-tested directly. */
export const handleResponseError = (error: unknown): Promise<never> => {
    if (isBearerUnauthenticated(error)) {
        try {
            unauthenticatedHandler?.(unauthenticatedReason(error));
        } catch (e) {
            // A failing handler must not turn one 401 into an unhandled crash.
        }
    }
    return Promise.reject(error);
};

/**
 * Installs both interceptors on [instance] (the default `axios` unless a
 * test passes its own). Idempotent per instance.
 */
export const installAuthInterceptors = (instance: AxiosInstance = axios): void => {
    if (!instance || !instance.interceptors) return;
    if (installedOn.has(instance)) return;
    installedOn.add(instance);
    instance.interceptors.request.use(attachBearer);
    instance.interceptors.response.use((response) => response, handleResponseError);
};
